/**
 * Weird Kart Racer — client orchestrator.
 *
 * Owns the Three.js scene, the post-processing chain, the chase camera, the
 * local kart (arcade physics), interpolated remote karts (driven by server
 * snapshots), host-simulated bots, the lobby/countdown/race/results state
 * machine, and checkpoint/lap tracking (the rules of which live in
 * src/progress.ts). It also drives the presentation layers
 * that hang off the simulation: per-track environment (src/env.ts), particles
 * and skid marks (src/fx.ts) and the synthesised engine mix (src/audio.ts).
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

import {
  BOT_SKILL_VALUES,
  DEFAULT_BOT_SKILL,
  TICK_HZ,
  TOTAL_LAPS,
  type BotState,
  type LobbyInfo,
  type RaceResume,
} from '../shared/protocol';
import {
  type KartState,
  type TrackData,
  type VehicleStats,
  type VehicleVisual,
} from './types';
import { VEHICLES, getVehicle, createVehicle, disposeVehicle } from './vehicles';
import { TRACKS, createTrack, passedGate, DEFAULT_TRACK_ID } from './track';
import { createKart, stepKart } from './physics';
import { createBot, stepBot, type Bot } from './bots';
import { Environment } from './env';
import { Effects } from './fx';
import { GameAudio } from './audio';
import { InputManager } from './input';
import { loadQuality, saveQuality, settingsFor, type QualityLevel } from './quality';
import { buildJoinUrl, clearJoinParam, parseJoinCode } from './joinlink';
import { Net } from './net';
import { UI } from './ui';
import { HUD } from './hud';
import { Minimap, type MinimapDot } from './minimap';
import { computePlace, type RacerProgress } from './placement';
import { LapTracker, type CheckpointState, type ProgressEvent } from './progress';

/** Nominal wheel radius used to convert ground speed into wheel spin. */
const WHEEL_RADIUS = 0.26;
/** Height at which tyre marks are laid, just above the tarmac. */
const SKID_Y = 0.075;
/** localStorage key for the identity token that survives reloads. */
const TOKEN_KEY = 'wkr-token';
/** localStorage key for the sign-in session, when the player has an account. */
const SESSION_KEY = 'wkr-session';
/** How long to wait for the server to confirm a stored sign-in before racing. */
const SESSION_RESUME_TIMEOUT_MS = 2500;

/**
 * A stable identity for this browser. The server keys a room's seats by it, so
 * a refresh mid-race reclaims the same car rather than joining as a stranger.
 *
 * Note this is a *seat* identity, not an account: it says "same browser", not
 * "this person". Owning a name is what the session token below is for.
 */
function loadToken(): string {
  try {
    const saved = window.localStorage.getItem(TOKEN_KEY);
    if (saved && saved.length >= 8) return saved;
    const fresh = crypto.randomUUID();
    window.localStorage.setItem(TOKEN_KEY, fresh);
    return fresh;
  } catch {
    // Private mode: a per-session token still works, it just won't survive.
    return crypto.randomUUID();
  }
}

/** The stored sign-in, if this browser has one. */
function loadSession(): string | null {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

/** Keep (or forget) the sign-in the server just handed us. */
function saveSession(session: string | null): void {
  try {
    if (session) window.localStorage.setItem(SESSION_KEY, session);
    else window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Private mode: the sign-in lasts as long as the tab does.
  }
}

const playerToken = loadToken();
/** Who we are signed in as, per the server. Null while racing as a guest. */
let account: string | null = null;
/** A lobby code from a `?join=` link, joined once the socket is up. */
const linkJoinCode = parseJoinCode(location.search);
let quality: QualityLevel = loadQuality();

// ---------------------------------------------------------------------------
// DOM + renderer
// ---------------------------------------------------------------------------
const canvas = document.getElementById('game') as HTMLCanvasElement;
const uiRoot = document.getElementById('ui-root') as HTMLElement;
const hudRoot = document.getElementById('hud-root') as HTMLElement;

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, settingsFor(quality).pixelRatio));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = settingsFor(quality).shadows;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
// Filmic response: highlights roll off instead of clipping, which is what makes
// the sunset sky and the neon kerbs read as light rather than flat colour.
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1;

const scene = new THREE.Scene();
// The sky dome (src/env.ts) is the backdrop; no flat clear colour needed.
scene.background = null;

// A near plane of 0.5 buys depth precision for the near-flat road/kerb/terrain
// stack — the chase camera never gets closer than that to anything anyway.
const camera = new THREE.PerspectiveCamera(62, window.innerWidth / window.innerHeight, 0.5, 2600);
camera.position.set(0, 60, 90);
camera.lookAt(0, 0, 0);

const environment = new Environment(scene, renderer);
const effects = new Effects(scene);
const audio = new GameAudio();

// ---------------------------------------------------------------------------
// Post-processing: bloom, strength driven by the current track's mood.
// ---------------------------------------------------------------------------
let composer: EffectComposer | null = null;
let bloomPass: UnrealBloomPass | null = null;

function setupComposer(): void {
  composer?.dispose();
  composer = null;
  bloomPass = null;
  if (!settingsFor(quality).bloom) return; // low/medium render straight to canvas

  try {
    const dpr = renderer.getPixelRatio();
    const target = new THREE.WebGLRenderTarget(
      Math.floor(window.innerWidth * dpr),
      Math.floor(window.innerHeight * dpr),
      { type: THREE.HalfFloatType, samples: settingsFor(quality).samples },
    );
    const c = new EffectComposer(renderer, target);
    c.addPass(new RenderPass(scene, camera));
    // High threshold: only genuinely bright things (sun, neon, boost) bloom.
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      currentTrack?.environment.bloom ?? 0.5,
      0.65,
      0.9,
    );
    c.addPass(bloom);
    c.addPass(new OutputPass());
    composer = c;
    bloomPass = bloom;
  } catch {
    // Any WebGL limitation here just means we render straight to the canvas.
    composer = null;
    bloomPass = null;
  }
}

/** Apply a graphics preset across the renderer, shadows, bloom and particles. */
function applyQuality(level: QualityLevel): void {
  quality = level;
  saveQuality(level);
  const s = settingsFor(level);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, s.pixelRatio));
  renderer.shadowMap.enabled = s.shadows;
  environment.setShadowQuality(s.shadows, s.shadowMapSize);
  effects.setBudget(s.particleScale);
  setupComposer();
  // Materials compiled against the old shadow setting need rebuilding.
  scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((m) => { m.needsUpdate = true; });
    else if (mat) mat.needsUpdate = true;
  });
}

// ---------------------------------------------------------------------------
// Minimap (2D overlay living in the HUD layer)
// ---------------------------------------------------------------------------
const minimap = new Minimap(hudRoot);

// ---------------------------------------------------------------------------
// Track (swappable — the host picks the map in the lobby)
// ---------------------------------------------------------------------------
/**
 * Built tracks, kept for the session. Building one costs ~200 ms (procedural
 * textures, terrain displacement, scenery placement), and the host cycling the
 * lobby's map selector would otherwise pay that on every press. There are only
 * four tracks, so the cache is naturally bounded and nothing needs evicting.
 */
const trackCache = new Map<string, TrackData>();

function getTrack(id: string): TrackData {
  let track = trackCache.get(id);
  if (!track) {
    track = createTrack(id);
    trackCache.set(id, track);
  }
  return track;
}

let currentTrackId = DEFAULT_TRACK_ID;
let currentTrack: TrackData = getTrack(currentTrackId);
scene.add(currentTrack.group);

const trackCenter = new THREE.Vector3();
let trackRadius = 200;

function refreshTrackDerived(): void {
  const box = new THREE.Box3().setFromPoints(currentTrack.centerline);
  box.getCenter(trackCenter);
  trackRadius = Math.max(box.getSize(new THREE.Vector3()).length() * 0.5, 120);

  const centerline = currentTrack.centerline.map((p) => ({ x: p.x, z: p.z }));
  const gates = currentTrack.checkpoints.map((c) => ({ x: c.position.x, z: c.position.z }));
  minimap.setTrack(centerline, gates[0], gates);

  environment.apply(currentTrack.environment);
  if (bloomPass) {
    bloomPass.strength = currentTrack.environment.bloom;
    bloomPass.enabled = currentTrack.environment.bloom > 0;
  }
}

// The composer wants the first track's bloom strength, so build it now that the
// track exists, then let applyQuality settle the rest of the preset.
applyQuality(quality);
refreshTrackDerived();

/** Swap to a different track (used when the lobby's selected map changes). */
function setTrack(id: string): void {
  if (id === currentTrackId) return;
  scene.remove(currentTrack.group);
  currentTrackId = id;
  currentTrack = getTrack(id);
  scene.add(currentTrack.group);
  effects.reset();
  refreshTrackDerived();
  resetProgress();
}

// ---------------------------------------------------------------------------
// Game state
// ---------------------------------------------------------------------------
type Phase = 'menu' | 'lobby' | 'countdown' | 'racing' | 'finished';
let phase: Phase = 'menu';

let currentLobby: LobbyInfo | null = null;

interface LocalKart {
  state: KartState;
  visual: VehicleVisual;
  stats: VehicleStats;
  /** Accumulated wheel rotation, in radians. */
  spin: number;
}
let local: LocalKart | null = null;

interface RemoteKart {
  visual: VehicleVisual;
  targetPos: THREE.Vector3;
  targetQuat: THREE.Quaternion;
  vel: THREE.Vector3;
  color: number;
  lap: number;
  cp: number;
  finished: boolean;
  spin: number;
  /** Seconds since this kart's last snapshot, for dead reckoning. */
  age: number;
}
const remotes = new Map<string, RemoteKart>();

/**
 * Bots this client simulates. Only the lobby host fills this: it steps them with
 * the same physics as a person and relays their transforms through the server,
 * so everyone races against the same field.
 */
const bots = new Map<string, Bot>();
/** True while we are the host and therefore responsible for the bots. */
let iAmHost = false;

/**
 * How far ahead a remote kart may be extrapolated, in seconds. Snapshots arrive
 * at TICK_HZ (50 ms apart), so a couple of missed packets stay covered while a
 * genuinely stalled connection stops rather than flying off across the map.
 */
const MAX_EXTRAPOLATION = 0.25;

// Race progress (local player). The lap/checkpoint rules — including what
// counts as a missed gate — live in src/progress.ts; this file only feeds it
// crossings and turns the events it hands back into sight and sound.
let tracker = new LapTracker(currentTrack.checkpoints.length, TOTAL_LAPS);
let finished = false;
let raceStartTime = 0;
let countdownEnd = 0;
let netAccum = 0;
/** Seconds until the wrong-way hint may fire again. */
let wrongWayCooldown = 0;
/** Length of the current frame, for helpers that run outside the loop body. */
let frameDt = 0;
let myFinishPlace: number | null = null;
/** Kart position at the previous progress check; gates are tested over the step. */
const lastProgressPos = new THREE.Vector3();

// Presentation timers.
let steerVisual = 0; // smoothed steering angle for the front wheels
let cameraFov = 62;
let shake = 0;
let scrapeCooldown = 0;
let bumpCooldown = 0;
let lastCountdownWord = '';
let wasBoosting = false;

const up = new THREE.Vector3(0, 1, 0);
const tmpFwd = new THREE.Vector3();
const tmpRight = new THREE.Vector3();
const tmpVec = new THREE.Vector3();
const tmpVec2 = new THREE.Vector3();

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// ---------------------------------------------------------------------------
// Networking
// ---------------------------------------------------------------------------
function resolveWsUrl(): string {
  const env = (import.meta.env.VITE_WS_URL as string | undefined) ?? undefined;
  if (env) return env;
  const override = new URLSearchParams(location.search).get('server');
  if (override) return override;
  // Connect same-origin at /ws. In dev, Vite proxies /ws to the Bun server on
  // :8080 (see vite.config.ts); in production the Bun server handles /ws itself.
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${location.host}/ws`;
}

const net = new Net();

/** Reconnect backoff, in ms, indexed by how many attempts have failed. */
const RECONNECT_DELAYS = [500, 1000, 2000, 4000, 8000];
let reconnectAttempt = 0;
let reconnectTimer: number | null = null;
/** Enough to rejoin the same seat after a drop. */
let lastJoin: { code: string; name: string; vehicleId: string } | null = null;

// ---------------------------------------------------------------------------
// UI + HUD
// ---------------------------------------------------------------------------
const hud = new HUD(hudRoot, {
  onToggleMute: () => setMuted(!audio.muted),
});
hud.setMuted(audio.muted);

function setMuted(muted: boolean): void {
  audio.setMuted(muted);
  hud.setMuted(muted);
}

const ui = new UI(uiRoot, VEHICLES, TRACKS, {
  onCreateLobby: (name, vehicleId) => {
    ui.setStatus('Creating lobby…');
    net.createLobby(name, vehicleId, playerToken);
  },
  onJoinLobby: (code, name, vehicleId) => {
    ui.setStatus('Joining lobby…');
    lastJoin = { code, name, vehicleId };
    net.joinLobby(code, name, vehicleId, playerToken);
  },
  onSelectVehicle: (vehicleId) => net.selectVehicle(vehicleId),
  onToggleReady: (ready) => net.setReady(ready),
  onStartRace: () => net.startRace(),
  onLeaveLobby: () => {
    net.leaveLobby();
    lastJoin = null;
    leaveToMenu();
  },
  onRematch: () => net.rematch(),
  onSelectTrack: (trackId) => net.selectTrack(trackId),
  onShowLeaderboard: (trackId) => net.getLeaderboard(trackId),
  onCloseLeaderboard: () => showCurrentScreen(),
  onSetBots: (count) => net.setBots(count),
  onSetBotSkill: (skill) => net.setBotSkill(skill),
  onSelectQuality: (level) => applyQuality(level),
  onShowAuth: () => ui.showAuth(),
  onCloseAuth: () => showCurrentScreen(),
  onLogin: (name, password) => net.login(name, password),
  onRegister: (name, password) => net.register(name, password),
  onLogout: () => net.logout(),
});
ui.setQuality(quality);

const input = new InputManager(hudRoot, {
  isTyping: () => ui.isInputActive(),
  onToggleMute: () => setMuted(!audio.muted),
});

/**
 * Show whichever screen the game state says we belong on. Used to come back from
 * an overlay (the leaderboard, signing in) and to repaint after something that
 * changes every screen, like signing in or out.
 */
function showCurrentScreen(): void {
  if (currentLobby && phase === 'finished') {
    ui.showResults(currentLobby, net.playerId ?? '');
  } else if (currentLobby && (phase === 'lobby' || currentLobby.state === 'lobby')) {
    ui.showLobby(currentLobby, net.playerId ?? '');
  } else if (phase === 'racing' || phase === 'countdown') {
    ui.hide(); // mid-race: the overlay has no business being up
  } else {
    ui.showMainMenu();
  }
}

function leaveToMenu(): void {
  phase = 'menu';
  currentLobby = null;
  syncJoinUrl(null); // stop the address bar advertising a lobby we have left
  clearKarts();
  effects.reset();
  audio.silenceDrive();
  input.setRacing(false);
  minimap.hide();
  hud.hide();
  hud.setCountdown(null);
  resetProgress(); // the menu's orbit shot shouldn't fly past a red gate
  ui.showMainMenu();
}

// ---------------------------------------------------------------------------
// Net event wiring
// ---------------------------------------------------------------------------
/**
 * The server's verdict on who we are signed in as. It arrives after a sign-in, a
 * sign-out, and once on every connect that replays a stored session — so this is
 * also where a session the server has forgotten gets dropped from the browser.
 */
net.on('auth', (msg) => {
  const wasSignedIn = account !== null;
  account = msg.account;
  saveSession(msg.session);
  ui.setAccount(msg.account);
  // Signing in changes the name on the kart and whether a run counts, which is
  // visible on every screen — so repaint whichever one we are on. This also
  // takes us off the sign-in screen once it has served its purpose.
  showCurrentScreen();
  // Set AFTER the repaint, or the rebuild wipes it.
  if (msg.account) ui.setStatus(`Signed in as ${msg.account} — your times count now.`);
  else if (wasSignedIn) ui.setStatus('Signed out — times are no longer saved.');
});

net.on('authError', (msg) => {
  ui.setAuthError(msg.message);
});

/**
 * Replay a stored sign-in on a fresh socket, resolving once the server has
 * answered. The account rides on the connection, so this has to happen before we
 * join anything — otherwise the first race of the session is run as a guest and
 * quietly not recorded. A server that never answers must not strand the player,
 * hence the timeout.
 */
function restoreSession(): Promise<void> {
  const session = loadSession();
  if (!session) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      off();
      window.clearTimeout(timer);
      resolve();
    };
    const off = net.on('auth', settle);
    const timer = window.setTimeout(settle, SESSION_RESUME_TIMEOUT_MS);
    net.resumeSession(session);
  });
}

/**
 * Keep the address bar pointing at the lobby we are in, so sharing is "send the
 * URL" and a reload walks straight back in. Cosmetic: a browser that refuses the
 * history call still has a working game and a working Copy link button.
 */
function syncJoinUrl(code: string | null): void {
  try {
    const next = code ? buildJoinUrl(location.href, code) : clearJoinParam(location.href);
    if (next !== location.href) history.replaceState(null, '', next);
  } catch {
    /* no history API (file://, sandboxed frame) — nothing depends on this */
  }
}

net.on('joined', (msg) => {
  currentLobby = msg.lobby;
  reconnectAttempt = 0;
  lastJoin = { code: msg.lobby.code, name: ui.playerName, vehicleId: myVehicleId(msg.lobby) };
  syncJoinUrl(msg.lobby.code);
  setTrack(msg.lobby.trackId);

  if (msg.resume && (msg.lobby.state === 'racing' || msg.lobby.state === 'countdown')) {
    // Dropped mid-race and came back: pick up where the server last saw us
    // rather than restarting on the grid.
    resumeRace(msg.resume);
    return;
  }

  phase = 'lobby';
  minimap.hide();
  hud.hide();
  input.setRacing(false);
  ui.showLobby(msg.lobby, net.playerId ?? '');
});

/** Our own vehicle id according to the lobby, for reconnect payloads. */
function myVehicleId(lobby: LobbyInfo): string {
  return lobby.players.find((p) => p.id === net.playerId)?.vehicleId ?? VEHICLES[0].id;
}

/** Drop straight back into a race in progress after a reconnect. */
function resumeRace(resume: RaceResume): void {
  spawnGrid();
  if (!local) return;

  const s = local.state;
  s.position.set(resume.p[0], 0, resume.p[2]);
  s.velocity.set(resume.v[0], 0, resume.v[2]);
  s.bodyQuat.set(resume.q[0], resume.q[1], resume.q[2], resume.q[3]);
  // Recover heading from the reported orientation so steering stays consistent.
  tmpFwd.set(0, 0, 1).applyQuaternion(s.bodyQuat);
  s.heading = Math.atan2(tmpFwd.x, tmpFwd.z);
  s.speed = s.velocity.length();
  local.visual.group.position.copy(s.position);
  local.visual.group.quaternion.copy(s.bodyQuat);

  tracker.resume(resume.lap, resume.cp);
  refreshCheckpointUi();
  lastProgressPos.copy(s.position);
  finished = false;
  myFinishPlace = null;

  phase = 'racing';
  raceStartTime = performance.now() - resume.elapsedMs;
  netAccum = 0;
  ui.hide();
  hud.show();
  minimap.show();
  input.setRacing(true);
  hud.flashMessage('Reconnected — back in the race', 2400);
}

net.on('lobbyUpdate', (msg) => {
  currentLobby = msg.lobby;
  iAmHost = msg.lobby.hostId === net.playerId;
  syncBotRoster(msg.lobby);
  if (msg.lobby.state === 'lobby') {
    // Reflect the host's map choice in the lobby preview.
    setTrack(msg.lobby.trackId);
    // Covers rematch resets and other players joining / readying up.
    if (phase !== 'lobby') {
      phase = 'lobby';
      clearKarts();
      effects.reset();
      audio.silenceDrive();
      minimap.hide();
      hud.hide();
      hud.setCountdown(null);
    }
    // A push, not a navigation: it must not close an overlay the player opened.
    ui.refreshLobby(msg.lobby, net.playerId ?? '');
  }
});

net.on('leaderboard', (msg) => {
  ui.showLeaderboard(msg.trackId, msg.entries, TRACKS);
});

net.on('error', (msg) => {
  ui.setError(msg.message);
  // A join that was refused (bad code, full, already started) leaves no seat to
  // hold, so don't let the reconnect logic keep chasing it.
  if (phase === 'menu' && !currentLobby) lastJoin = null;
});

net.on('countdown', () => startCountdown());
net.on('raceStart', () => beginRace());

net.on('snapshot', (msg) => {
  if (!currentLobby) return;
  for (const e of msg.players) {
    if (e.id === net.playerId) continue;
    // Our own bots come back to us in the snapshot; we already have the real
    // thing locally, so ignore the echo rather than drawing it twice.
    if (bots.has(e.id)) continue;
    let r = remotes.get(e.id);
    if (!r) {
      const vid = currentLobby.players.find((p) => p.id === e.id)?.vehicleId ?? 'duck';
      r = createRemote(
        vid,
        new THREE.Vector3(e.p[0], e.p[1], e.p[2]),
        new THREE.Quaternion(e.q[0], e.q[1], e.q[2], e.q[3]),
      );
      remotes.set(e.id, r);
    }
    r.targetPos.set(e.p[0], e.p[1], e.p[2]);
    r.targetQuat.set(e.q[0], e.q[1], e.q[2], e.q[3]);
    r.vel.set(e.v[0], e.v[1], e.v[2]);
    r.age = 0;
    r.lap = e.lap;
    r.cp = e.cp;
    r.finished = e.fin;
  }
});

net.on('playerFinished', (msg) => {
  if (msg.id === net.playerId) {
    myFinishPlace = msg.place; // authoritative place from the server
    hud.flashMessage(`You finished ${ordinal(msg.place)}!`, 2600);
  }
});

net.on('raceFinished', (msg) => {
  currentLobby = msg.lobby;
  phase = 'finished';
  audio.silenceDrive();
  minimap.hide();
  hud.setCountdown(null);
  ui.showResults(msg.lobby, net.playerId ?? '');
});

net.on('playerLeft', (msg) => {
  const r = remotes.get(msg.id);
  if (r) {
    scene.remove(r.visual.group);
    disposeVehicle(r.visual);
    remotes.delete(msg.id);
  }
});

net.onClose(() => {
  audio.silenceDrive();
  if (lastJoin) {
    // We were in a lobby: the seat is held server-side for a grace period, so
    // try to climb back into it before giving up and dumping the player out.
    scheduleReconnect();
    return;
  }
  if (phase !== 'menu') leaveToMenu(); // returns to a fresh main menu
  ui.setError('Disconnected from the server.'); // set AFTER the re-render so it shows
});

/** Retry the socket with backoff, then rejoin the same seat by token. */
function scheduleReconnect(): void {
  if (reconnectTimer !== null) return;
  const join = lastJoin;
  if (!join) return;

  if (reconnectAttempt >= RECONNECT_DELAYS.length) {
    lastJoin = null;
    reconnectAttempt = 0;
    if (phase !== 'menu') leaveToMenu();
    ui.setError('Lost connection to the server.');
    return;
  }

  const delay = RECONNECT_DELAYS[reconnectAttempt];
  reconnectAttempt++;
  hud.flashMessage('Connection lost — reconnecting…', delay + 400);

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    net
      .connect(resolveWsUrl())
      // The account lives on the connection, so a new socket is a guest until
      // the session is replayed — do that before reclaiming the seat, or the
      // race we are dropping back into stops counting.
      .then(() => restoreSession())
      .then(() => net.joinLobby(join.code, join.name, join.vehicleId, playerToken))
      .catch(() => scheduleReconnect());
  }, delay);
}

// ---------------------------------------------------------------------------
// Kart spawning / teardown
// ---------------------------------------------------------------------------
function clearKarts(): void {
  if (local) {
    scene.remove(local.visual.group);
    disposeVehicle(local.visual);
    local = null;
  }
  for (const r of remotes.values()) {
    scene.remove(r.visual.group);
    disposeVehicle(r.visual);
  }
  remotes.clear();
  clearBots();
  effects.endSkid();
}

function clearBots(): void {
  for (const bot of bots.values()) {
    scene.remove(bot.visual.group);
    disposeVehicle(bot.visual);
  }
  bots.clear();
}

/**
 * Keep the simulated bot set in step with the lobby roster.
 *
 * Only the host simulates: everyone else receives bots as ordinary remote karts
 * through the snapshot stream, so the roster is identical on every screen.
 */
function syncBotRoster(lobby: LobbyInfo): void {
  if (!iAmHost) {
    clearBots();
    return;
  }
  const wanted = new Set(lobby.players.filter((p) => p.isBot).map((p) => p.id));
  for (const [id, bot] of bots) {
    if (wanted.has(id)) continue;
    scene.remove(bot.visual.group);
    disposeVehicle(bot.visual);
    bots.delete(id);
  }
}

function yawQuat(heading: number): THREE.Quaternion {
  return new THREE.Quaternion().setFromAxisAngle(up, heading);
}

function createRemote(vehicleId: string, pos: THREE.Vector3, quat: THREE.Quaternion): RemoteKart {
  const visual = createVehicle(vehicleId);
  visual.group.position.copy(pos);
  visual.group.quaternion.copy(quat);
  scene.add(visual.group);
  return {
    visual,
    targetPos: pos.clone(),
    targetQuat: quat.clone(),
    vel: new THREE.Vector3(),
    color: getVehicle(vehicleId).color,
    lap: 0,
    cp: 0,
    finished: false,
    spin: 0,
    age: 0,
  };
}

/**
 * Skill for the bot in grid slot `index`, from the lobby's setting.
 *
 * The setting is the centre of a band rather than a single number: a field of
 * identical drivers runs nose-to-tail all race, which reads as a train rather
 * than a race. The spread is deterministic in the slot index, so a rematch on
 * the same settings produces the same field.
 */
function botSkillFor(index: number): number {
  const base = BOT_SKILL_VALUES[currentLobby?.botSkill ?? DEFAULT_BOT_SKILL];
  const spread = (((index * 37) % 5) - 2) * 0.05; // -0.10 .. +0.10
  // Never above the top tier: the raw scale keeps going, but past the Ace value
  // a bot over-commits and starts losing time rather than gaining it.
  return Math.min(BOT_SKILL_VALUES.ace, clamp01(base + spread));
}

function spawnGrid(): void {
  clearKarts();
  if (!currentLobby) return;
  setTrack(currentLobby.trackId); // make sure we race the lobby's chosen map
  const myId = net.playerId;
  const slots = currentTrack.startPositions;
  currentLobby.players.forEach((p, i) => {
    const slot = slots[i % slots.length];
    if (p.id === myId) {
      const stats = getVehicle(p.vehicleId);
      const state = createKart(slot.position.clone(), slot.heading);
      const visual = createVehicle(p.vehicleId);
      visual.group.position.copy(state.position);
      visual.group.quaternion.copy(state.bodyQuat);
      scene.add(visual.group);
      local = { state, visual, stats, spin: 0 };
      resetProgress();
      finished = false;
      myFinishPlace = null;
      // Seed the gate test from the grid slot, or the first step would be
      // measured against wherever the previous race left off.
      lastProgressPos.copy(state.position);
    } else if (p.isBot && iAmHost) {
      // We simulate this one; everyone else will see it via our snapshots.
      const bot = createBot(
        p.id, p.vehicleId, slot.position, slot.heading, botSkillFor(i), currentTrack, TOTAL_LAPS,
      );
      scene.add(bot.visual.group);
      bots.set(p.id, bot);
    } else {
      remotes.set(p.id, createRemote(p.vehicleId, slot.position.clone(), yawQuat(slot.heading)));
    }
  });
}

/** Advance every bot we own and report them to the server. */
function updateBots(dt: number): void {
  if (bots.size === 0) return;
  for (const bot of bots.values()) {
    if (stepBot(bot, currentTrack, dt)) net.sendBotFinished(bot.id);
    bot.spin = animateKart(
      bot.visual, bot.spin, bot.state.speed, -bot.steer * 0.5,
      bot.state.boosting ? 1 : 0, bot.state.braking, dt,
    );
    emitTyreEffects(
      bot.visual, bot.id, bot.state.velocity.length(),
      bot.state.slip, bot.state.offRoad, bot.state.heading,
    );
  }
}

/** Relay the bots' transforms at the same rate we report our own. */
function sendBotStates(): void {
  if (bots.size === 0) return;
  const payload: BotState[] = [];
  for (const bot of bots.values()) {
    const s = bot.state;
    payload.push({
      id: bot.id,
      p: [s.position.x, s.position.y, s.position.z],
      q: [s.bodyQuat.x, s.bodyQuat.y, s.bodyQuat.z, s.bodyQuat.w],
      v: [s.velocity.x, s.velocity.y, s.velocity.z],
      lap: bot.progress.lap,
      cp: bot.progress.lastCp,
    });
  }
  net.sendBotStates(payload);
}

// ---------------------------------------------------------------------------
// Race lifecycle
// ---------------------------------------------------------------------------
function startCountdown(): void {
  phase = 'countdown';
  currentLobby && spawnGrid();
  effects.reset();
  ui.hide();
  hud.show();
  minimap.show();
  input.setRacing(true);
  countdownEnd = performance.now() + 3200;
  lastCountdownWord = '';
}

function beginRace(): void {
  if (!local) spawnGrid();
  phase = 'racing';
  finished = false;
  myFinishPlace = null;
  resetProgress();
  if (local) lastProgressPos.copy(local.state.position);
  raceStartTime = performance.now();
  netAccum = 0;
  effects.endSkid();
  ui.hide();
  hud.show();
  input.setRacing(true);
  hud.setCountdown('GO!');
  window.setTimeout(() => hud.setCountdown(null), 700);
  hud.flashMessage('Drive · boost · drift — good luck', 2600);
}

function finishRace(): void {
  finished = true;
  // The server times the run against its own clock, so nothing is sent here but
  // the fact that we crossed the line.
  net.sendFinished();
  hud.flashMessage('Finish line! Waiting for others…', 2600);
}

// ---------------------------------------------------------------------------
// Progress / placement
// ---------------------------------------------------------------------------
function computeMyPlace(): number {
  if (!local) return 1;
  const cps = currentTrack.checkpoints.map((c) => ({ x: c.position.x, z: c.position.z }));
  const me: RacerProgress = {
    lap: tracker.lap,
    cp: tracker.lastCp,
    x: local.state.position.x,
    z: local.state.position.z,
    finished,
    finishPlace: myFinishPlace,
  };
  const others: RacerProgress[] = [];
  for (const r of remotes.values()) {
    others.push({
      lap: r.lap,
      cp: r.cp,
      x: r.visual.group.position.x,
      z: r.visual.group.position.z,
      finished: r.finished,
      finishPlace: null,
    });
  }
  // Bots we simulate are not echoed back to us as remotes, so count them here.
  for (const bot of bots.values()) {
    others.push({
      lap: bot.progress.lap,
      cp: bot.progress.lastCp,
      x: bot.state.position.x,
      z: bot.state.position.z,
      finished: bot.progress.finished,
      finishPlace: null,
    });
  }
  return computePlace(me, others, cps);
}

/**
 * Rebuild the tracker for the current circuit and repaint every surface that
 * shows checkpoint state: the gates on track, the minimap ticks and the HUD pips.
 */
function resetProgress(): void {
  if (tracker.count !== currentTrack.checkpoints.length) {
    tracker = new LapTracker(currentTrack.checkpoints.length, TOTAL_LAPS);
  } else {
    tracker.reset();
  }
  refreshCheckpointUi();
}

/**
 * Push the tracker's view of the lap out to all three displays at once. They
 * share a colour language — amber next, green passed, red missed — so wherever
 * the driver happens to be looking, the answer is the same.
 */
function refreshCheckpointUi(): void {
  const states = tracker.states();
  currentTrack.checkpointMarkers.setStates(states);
  minimap.setCheckpointStates(states);
  hud.setCheckpoints(hudCheckpointOrder(states));
}

/**
 * States in the order they are driven through: gate 1 first, the start/finish
 * line last. That is what makes the HUD row read left-to-right as a lap.
 */
function hudCheckpointOrder(states: CheckpointState[]): CheckpointState[] {
  const ordered = states.slice(1);
  ordered.push(states[0]);
  return ordered;
}

/** Turn one tracker event into whatever the driver should see and hear. */
function handleProgressEvent(event: ProgressEvent): void {
  const markers = currentTrack.checkpointMarkers;
  switch (event.type) {
    case 'checkpoint':
      // No banner: the gate flaring up, the pip filling in and the chime are
      // already three confirmations, and this fires a dozen times a lap.
      markers.pulse(event.index);
      audio.checkpoint(false);
      break;

    case 'missed': {
      const n = event.indices.length;
      hud.flashMessage(
        `Checkpoint missed! Go back for ${n === 1 ? 'it' : 'them'}`,
        2200,
        true,
      );
      audio.checkpoint(true);
      shake = Math.max(shake, 0.25);
      break;
    }

    case 'recovered':
      markers.pulse(event.index);
      audio.checkpoint(false);
      hud.flashMessage(
        event.outstanding === 0
          ? 'Checkpoint collected — lap is alive again'
          : `Checkpoint collected — ${event.outstanding} still missing`,
        1600,
      );
      break;

    case 'lap':
      markers.pulse(0);
      audio.checkpoint(false);
      hud.flashMessage(`Lap ${event.lap + 1} / ${TOTAL_LAPS}`);
      break;

    case 'lapVoid':
      // Crossing the line with gates outstanding is the case that looks broken
      // from the driver's seat, so name it rather than silently doing nothing.
      audio.checkpoint(true);
      hud.flashMessage(
        `Lap not counted — ${event.missed} checkpoint${event.missed === 1 ? '' : 's'} missed`,
        2600,
        true,
      );
      break;

    case 'finish':
      markers.pulse(0);
      finishRace();
      break;
  }
}

function updateProgress(): void {
  if (!local) return;
  const cps = currentTrack.checkpoints;

  // Every gate is tested, not just the expected one: driving around a
  // checkpoint is only detectable by seeing a LATER gate go by, and that is
  // exactly the moment the driver needs to be told, rather than a corner later
  // when the finish line quietly refuses to count the lap.
  const crossed: number[] = [];
  for (let i = 0; i < cps.length; i++) {
    if (passedGate(cps[i], lastProgressPos, local.state.position)) crossed.push(i);
  }
  if (crossed.length > 1) {
    // Two gates in one step needs a pathological frame, but if it happens they
    // have to be fed in the order they were actually driven through.
    const from = tracker.nextCp;
    const n = cps.length;
    crossed.sort((a, b) => ((a - from + n) % n) - ((b - from + n) % n));
  }
  for (const index of crossed) {
    for (const event of tracker.cross(index)) handleProgressEvent(event);
  }
  if (crossed.length > 0) refreshCheckpointUi();

  // Light-touch wrong-way hint. Counted in seconds, not frames — otherwise the
  // hint repeats twice as often on a 120 Hz display as on a 60 Hz one.
  wrongWayCooldown -= frameDt;
  if (!finished && local.state.speed > 6 && wrongWayCooldown <= 0) {
    const s = currentTrack.sample(local.state.position);
    tmpFwd.set(Math.sin(local.state.heading), 0, Math.cos(local.state.heading));
    if (s.forward.dot(tmpFwd) < -0.35) {
      // Silent while a gate is owed: going back for it *is* the wrong way, and
      // shouting about it would bury the message telling them to.
      if (tracker.outstanding === 0) hud.flashMessage('Wrong way!', 900, true);
      wrongWayCooldown = 1;
    }
  }

  lastProgressPos.copy(local.state.position);
}

function sendMyState(): void {
  if (!local) return;
  const s = local.state;
  net.sendState(
    [s.position.x, s.position.y, s.position.z],
    [s.bodyQuat.x, s.bodyQuat.y, s.bodyQuat.z, s.bodyQuat.w],
    [s.velocity.x, s.velocity.y, s.velocity.z],
    tracker.lap,
    tracker.lastCp,
  );
}

// ---------------------------------------------------------------------------
// Kart presentation
// ---------------------------------------------------------------------------

/**
 * Spin the wheels, turn the steered pair, and set the emissive levels for boost
 * and braking. `groundSpeed` is signed along the kart's forward axis.
 */
function animateKart(
  visual: VehicleVisual,
  spin: number,
  groundSpeed: number,
  steerAngle: number,
  boost01: number,
  braking: boolean,
  dt: number,
): number {
  const next = spin + (groundSpeed / WHEEL_RADIUS) * dt;
  for (const wheel of visual.wheels) wheel.rotation.x = next;
  for (const wheel of visual.steered) wheel.rotation.y = steerAngle;
  for (const material of visual.glow) material.emissiveIntensity = 0.15 + boost01 * 2.6;
  for (const material of visual.brakeLights) material.emissiveIntensity = braking ? 2.6 : 0.12;
  return next;
}

/**
 * Tyre smoke, dirt and rubber for one kart. `slip` is sideways scrub in
 * units/second, `offRoad` is 0 on tarmac and ramps to 1 in the dirt.
 */
function emitTyreEffects(
  visual: VehicleVisual,
  key: string,
  speed: number,
  slip: number,
  offRoad: number,
  heading: number,
): void {
  if (speed < 3.5) return;
  const dusty = offRoad > 0.05;
  const sliding = slip > 4;
  if (!dusty && !sliding) return;

  const dustColor = currentTrack.environment.dustColor;
  tmpRight.set(Math.cos(heading), 0, -Math.sin(heading));
  const intensity = clamp01(dusty ? offRoad : (slip - 4) / 14);

  visual.group.updateMatrixWorld();
  for (let i = 0; i < visual.contacts.length; i++) {
    tmpVec.copy(visual.contacts[i]).applyMatrix4(visual.group.matrixWorld);
    if (dusty) {
      effects.dust(tmpVec.x, tmpVec.y, tmpVec.z, dustColor, intensity);
    } else {
      effects.tyreSmoke(tmpVec.x, tmpVec.y, tmpVec.z, intensity);
      tmpVec2.set(tmpVec.x, SKID_Y, tmpVec.z);
      effects.skid(`${key}:${i}`, tmpVec2, tmpRight, 0.34);
    }
  }
}

/** Fire the afterburner out of every exhaust anchor. */
function emitBoostFlames(visual: VehicleVisual, heading: number): void {
  visual.group.updateMatrixWorld();
  tmpFwd.set(Math.sin(heading), 0, Math.cos(heading));
  for (const anchor of visual.exhausts) {
    tmpVec.copy(anchor).applyMatrix4(visual.group.matrixWorld);
    effects.flame(tmpVec.x, tmpVec.y, tmpVec.z, -tmpFwd.x, 0.1, -tmpFwd.z, 1);
  }
}

// ---------------------------------------------------------------------------
// Per-frame updates
// ---------------------------------------------------------------------------
function updateRemotes(dt: number): void {
  const a = 1 - Math.exp(-dt * 14);
  for (const [id, r] of remotes) {
    const group = r.visual.group;

    // Dead reckoning. Snapshots are 50 ms apart, so simply chasing the last
    // reported position leaves every other kart permanently that far in the
    // past and visibly rubber-banding. Carrying the reported velocity forward
    // by the snapshot's age closes most of that gap for free — the data is
    // already on the wire.
    r.age = Math.min(r.age + dt, MAX_EXTRAPOLATION);
    tmpVec.copy(r.targetPos).addScaledVector(r.vel, r.age);
    group.position.lerp(tmpVec, a);
    group.quaternion.slerp(r.targetQuat, a);

    // Remote inputs aren't on the wire, so read the kart's behaviour instead:
    // forward speed and sideways scrub both fall out of velocity vs facing.
    tmpFwd.set(0, 0, 1).applyQuaternion(group.quaternion);
    tmpFwd.y = 0;
    tmpFwd.normalize();
    const forwardSpeed = r.vel.dot(tmpFwd);
    tmpVec.copy(r.vel).addScaledVector(tmpFwd, -forwardSpeed);
    const slip = tmpVec.length();
    const heading = Math.atan2(tmpFwd.x, tmpFwd.z);

    r.spin = animateKart(r.visual, r.spin, forwardSpeed, 0, 0, false, dt);
    if (phase === 'racing') {
      const off = clamp01((currentTrack.sample(group.position).distance - currentTrack.roadWidth / 2) / 6);
      emitTyreEffects(r.visual, id, r.vel.length(), slip, off, heading);
    }
  }
}

function updateMinimap(): void {
  if (!local) return;
  const dots: MinimapDot[] = [
    { x: local.state.position.x, z: local.state.position.z, color: local.stats.color, self: true },
  ];
  for (const r of remotes.values()) {
    dots.push({
      x: r.visual.group.position.x,
      z: r.visual.group.position.z,
      color: r.color,
      self: false,
    });
  }
  for (const bot of bots.values()) {
    dots.push({
      x: bot.state.position.x,
      z: bot.state.position.z,
      color: bot.stats.color,
      self: false,
    });
  }
  minimap.update(dots);
}

// Kart-vs-kart ramming. Each client pushes its OWN kart out of any overlap with
// a remote kart; because every client does the same against everyone else's
// ghost, bumps are felt on both sides (with a small network delay).
const KART_HALF = 1.4; // collision half-extent (karts are ~2 long / 1.2 wide)
function resolveCollisions(dt: number): void {
  if (!local) return;
  bumpCooldown -= dt;
  const minDist = KART_HALF * 2;
  const minDist2 = minDist * minDist;
  for (const r of remotes.values()) {
    const other = r.visual.group.position;
    const dx = local.state.position.x - other.x;
    const dz = local.state.position.z - other.z;
    const d2 = dx * dx + dz * dz;
    if (d2 >= minDist2 || d2 < 1e-6) continue;
    const d = Math.sqrt(d2);
    const nx = dx / d;
    const nz = dz / d;
    // Push fully out of the overlap.
    const overlap = minDist - d;
    local.state.position.x += nx * overlap;
    local.state.position.z += nz * overlap;
    // Chunky bump: outward impulse + a little speed scrub.
    const impulse = 5 + Math.abs(local.state.speed) * 0.4;
    local.state.velocity.x += nx * impulse;
    local.state.velocity.z += nz * impulse;
    local.state.speed *= 0.85;

    if (bumpCooldown <= 0) {
      bumpCooldown = 0.18;
      const force = clamp01(Math.abs(local.state.speed) / 30);
      effects.sparks(
        (local.state.position.x + other.x) / 2,
        0.4,
        (local.state.position.z + other.z) / 2,
        nx, nz, force,
      );
      audio.impact(0.4 + force * 0.6);
      shake = Math.max(shake, 0.25 + force * 0.5);
    }
  }
  local.state.position.y = 0;
}

/** Sparks and a scrape when the kart is grinding along the armco. */
function checkBarrierScrape(dt: number): void {
  if (!local) return;
  scrapeCooldown -= dt;
  const s = currentTrack.sample(local.state.position);
  if (s.distance < currentTrack.wallRadius - 0.15) return;
  if (Math.abs(local.state.speed) < 7 || scrapeCooldown > 0) return;

  scrapeCooldown = 0.09;
  tmpVec.copy(local.state.position).sub(s.point).setY(0).normalize();
  const force = clamp01(Math.abs(local.state.speed) / 40);
  effects.sparks(
    local.state.position.x, 0.35, local.state.position.z,
    -tmpVec.x, -tmpVec.z, force * 0.7,
  );
  audio.impact(0.18 + force * 0.25);
  shake = Math.max(shake, 0.12 + force * 0.2);
}

function updateCamera(dt: number, now: number): void {
  if (local && (phase === 'racing' || phase === 'countdown' || phase === 'finished')) {
    const s = local.state;
    const speed01 = clamp01(Math.abs(s.speed) / local.stats.maxSpeed);
    tmpFwd.set(Math.sin(s.heading), 0, Math.cos(s.heading));

    // Pull back and drop slightly as speed builds — the classic sense of the
    // camera being dragged along behind the kart.
    const back = 9.2 + speed01 * 3.4;
    const height = 4.4 + speed01 * 0.8;
    tmpVec.copy(s.position).addScaledVector(tmpFwd, -back);
    tmpVec.y += height;
    const follow = 1 - Math.exp(-dt * (5.5 + speed01 * 3.5));
    camera.position.lerp(tmpVec, follow);

    // Look further ahead the faster we go, so corners arrive with warning.
    tmpVec.copy(s.position).addScaledVector(tmpFwd, 6 + speed01 * 6);
    tmpVec.y += 1.3;
    camera.lookAt(tmpVec);

    // Field of view stretches with speed and snaps wider under boost.
    const targetFov = 62 + speed01 * 11 + (s.boosting ? 6 : 0);
    cameraFov += (targetFov - cameraFov) * (1 - Math.exp(-dt * 5));

    // Vibration from boost, dirt and impacts, decaying every frame.
    shake = Math.max(shake * Math.exp(-dt * 6), (s.boosting ? 0.10 : 0) + s.offRoad * speed01 * 0.3);
    if (shake > 0.002) {
      camera.position.x += (Math.random() - 0.5) * shake;
      camera.position.y += (Math.random() - 0.5) * shake;
      camera.position.z += (Math.random() - 0.5) * shake;
    }
  } else {
    // Slow cinematic orbit over the circuit for the menu / lobby.
    const t = now * 0.00013;
    camera.position.set(
      trackCenter.x + Math.cos(t) * trackRadius * 0.95,
      trackCenter.y + trackRadius * 0.42 + Math.sin(t * 1.7) * 12,
      trackCenter.z + Math.sin(t) * trackRadius * 0.95,
    );
    camera.lookAt(trackCenter.x, 6, trackCenter.z);
    cameraFov += (58 - cameraFov) * (1 - Math.exp(-dt * 3));
    shake = 0;
  }

  if (Math.abs(camera.fov - cameraFov) > 0.01) {
    camera.fov = cameraFov;
    camera.updateProjectionMatrix();
  }
}

function updateHud(now: number): void {
  if (!local || !currentLobby) return;
  const speed = local.state.velocity.length();
  hud.update({
    // Ground speed (velocity magnitude) so the readout matches real movement,
    // even mid-drift. boost is a 0..100 percentage for the HUD bar.
    speed,
    lap: Math.min(tracker.lap + 1, TOTAL_LAPS),
    totalLaps: TOTAL_LAPS,
    place: computeMyPlace(),
    totalPlayers: currentLobby.players.length,
    time: now - raceStartTime,
    boost: local.state.boostMeter * 100,
    boosting: local.state.boosting,
    // Drives the speed vignette: nothing at a crawl, heavy at the limiter.
    strain: clamp01((speed / local.stats.maxSpeed - 0.45) / 0.75),
  });
}

function updateCountdown(now: number): void {
  if (phase !== 'countdown') return;
  const remain = countdownEnd - now;
  const n = Math.ceil(remain / 1000);
  const word = n > 0 ? String(n) : 'GO!';
  if (word !== lastCountdownWord) {
    lastCountdownWord = word;
    hud.setCountdown(word);
    audio.countdown(word === 'GO!');
  }
}

function updateAudio(throttle: number): void {
  if (!local || phase !== 'racing') return;
  const s = local.state;
  const speed01 = clamp01(Math.abs(s.speed) / local.stats.maxSpeed);
  audio.updateDrive({
    // Revs track speed but never drop to nothing — the motor is always running.
    rpm: 0.12 + speed01 * 0.88,
    load: Math.max(0, throttle),
    slip: s.slip,
    offRoad: s.offRoad,
    boosting: s.boosting,
    speed01,
  });
}

// ---------------------------------------------------------------------------
// Main loop
// ---------------------------------------------------------------------------
let last = performance.now();
function frame(now: number): void {
  requestAnimationFrame(frame);
  const dt = Math.min((now - last) / 1000, 0.05);
  last = now;
  frameDt = dt;

  if (local && phase === 'racing') {
    const driving = input.read();
    const s = local.state;
    stepKart(s, driving, local.stats, currentTrack, dt);
    resolveCollisions(dt);
    checkBarrierScrape(dt);
    local.visual.group.position.copy(s.position);
    local.visual.group.quaternion.copy(s.bodyQuat);

    // Front wheels chase the steering input rather than snapping to it.
    const targetSteer = -driving.steer * 0.5;
    steerVisual += (targetSteer - steerVisual) * (1 - Math.exp(-dt * 12));
    local.spin = animateKart(
      local.visual, local.spin, s.speed, steerVisual,
      s.boosting ? 1 : 0, s.braking, dt,
    );

    emitTyreEffects(local.visual, 'me', s.velocity.length(), s.slip, s.offRoad, s.heading);
    if (s.boosting) emitBoostFlames(local.visual, s.heading);
    if (s.boosting && !wasBoosting) audio.boost();
    wasBoosting = s.boosting;

    if (!finished) updateProgress();
    updateBots(dt);
    netAccum += dt;
    if (netAccum >= 1 / TICK_HZ) {
      netAccum -= 1 / TICK_HZ;
      sendMyState();
      sendBotStates();
    }
    updateHud(now);
    updateAudio(driving.throttle);
  } else if (local) {
    local.visual.group.position.copy(local.state.position);
    local.visual.group.quaternion.copy(local.state.bodyQuat);
  }

  updateRemotes(dt);
  updateCountdown(now);
  updateCamera(dt, now);
  effects.update(dt);
  currentTrack.checkpointMarkers.update(dt);
  if (local && (phase === 'racing' || phase === 'countdown')) updateMinimap();

  environment.update(camera, local ? local.state.position : trackCenter);

  if (composer) composer.render(dt);
  else renderer.render(scene, camera);
}

// ---------------------------------------------------------------------------
// Helpers / boot
// ---------------------------------------------------------------------------
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

/**
 * Follow the window wherever it goes. Dragging it to a second monitor changes
 * the aspect ratio AND, if the two screens scale differently, devicePixelRatio —
 * and a stale pixel ratio is the one that shows, as a canvas that is suddenly
 * soft or needlessly expensive. The DOM layers need no help: they are sized in
 * rem off a viewport-derived root font size (src/style.css).
 */
function handleViewportChange(): void {
  const width = window.innerWidth;
  const height = window.innerHeight;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, settingsFor(quality).pixelRatio));
  renderer.setSize(width, height);
  composer?.setSize(width, height);
}

window.addEventListener('resize', handleViewportChange);

/**
 * Watch for the display density changing under us. A window dragged onto a
 * monitor at a different scale factor does not reliably fire `resize` — the CSS
 * viewport can come out identical — but the pixel ratio it is being rasterised
 * at has changed, so the canvas has to be rebuilt at the new density. The query
 * is re-armed each time, since it is pinned to the ratio that was current.
 */
function watchPixelRatio(): void {
  if (typeof window.matchMedia !== 'function') return;
  const query = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
  query.addEventListener(
    'change',
    () => {
      handleViewportChange();
      watchPixelRatio();
    },
    { once: true },
  );
}
watchPixelRatio();

ui.showMainMenu();
ui.setStatus('Connecting…');
// A ?join= link should show its code in the box straight away, so a lobby that
// has since died leaves something to retry rather than an empty menu.
if (linkJoinCode) ui.setJoinCode(linkJoinCode);
requestAnimationFrame(frame);

net
  .connect(resolveWsUrl())
  .then(() => restoreSession())
  .then(() => {
    if (!linkJoinCode) {
      ui.setStatus(
        account
          ? `Signed in as ${account} — create or join a lobby!`
          : 'Connected. Create or join a lobby!',
      );
      return;
    }
    // Arrived on somebody's link: walk straight into their lobby.
    ui.setStatus(`Joining lobby ${linkJoinCode}…`);
    lastJoin = { code: linkJoinCode, name: ui.playerName, vehicleId: ui.selectedVehicle };
    net.joinLobby(linkJoinCode, ui.playerName, ui.selectedVehicle, playerToken);
  })
  .catch(() => ui.setError('Could not reach the game server. Is it running?'));
